import { useMemo, useState } from "react";
import { useIntl } from "react-intl";
import { useNavigate, useParams } from "react-router-dom";
import Page from "~Common/Page";
import { DataTable } from "~components/DataTable/DataTable.tsx";
import { DEFAULT_PAGE_SIZE_OPTIONS } from "~constants/pagination.ts";
import { PermissionNames } from "~enums/permissions.ts";
import { useHandlePagination } from "~hooks/useHandlePagination.ts";
import { useSportConfiguration } from "~hooks/useSportConfiguration.ts";
import { useFetchLeaderCriteria } from "~pages/LeaderCriteria/hooks/useFetchLeaderCriteria.ts";
import { useGetStatisticFieldLabel } from "~pages/LeaderCriteria/hooks/useGetStatisticFieldLabel.ts";
import { LeaderCriteriaRuleEditModal } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/components/LeaderCriteriaRuleEditModal.tsx";
import { useDeleteLeaderCriteriaRule } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/hooks/useDeleteLeaderCriteriaRule.ts";
import { useFetchLeaderCriteriaRules } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/hooks/useFetchLeaderCriteriaRules.ts";
import { useGetLeaderCriteriaRuleTableColumns } from "~pages/LeaderCriteria/LeaderCriteriaRuleListPage/hooks/useGetLeaderCriteriaRuleTableColumns.tsx";
import type { LeaderCriteriaRule } from "~pages/LeaderCriteria/types/leaderCriteriaRule.ts";

const LeaderCriteriaRuleListPage = () => {
  const paginationConfig = useHandlePagination(10, 1, DEFAULT_PAGE_SIZE_OPTIONS);
  const { sport, customerId, organizationId, leaderCriteriaId } = useParams();
  const { formatMessage } = useIntl();
  const navigate = useNavigate();
  const [ruleToEdit, setRuleToEdit] = useState<LeaderCriteriaRule | null>(null);

  const { uiConfiguration } = useSportConfiguration();
  const dataTableUiConfiguration = uiConfiguration.pages.leaderCriteria.ruleListPage;
  const columns = useGetLeaderCriteriaRuleTableColumns();
  const getStatisticFieldLabel = useGetStatisticFieldLabel();

  const listParams = useMemo(
    () => ({
      limit: paginationConfig.pageSize.toString(),
      offset: paginationConfig.offset.toString(),
    }),
    [paginationConfig.pageSize, paginationConfig.offset],
  );

  const { data: leaderCriteria } = useFetchLeaderCriteria(leaderCriteriaId);
  const { data, refetch, isPending } = useFetchLeaderCriteriaRules({ customParams: listParams });
  const { mutate: deleteLeaderCriteriaRule, isPending: isDeletePending } = useDeleteLeaderCriteriaRule();

  const pageTitle = formatMessage(
    { id: `pageTitles.organization.leader.${leaderCriteria ? "rules" : "empty"}` },
    { ruleName: leaderCriteria?.name },
  );

  const getNavigateUrl = (leaderCriteriaId = "") =>
    `/${customerId}/admin/${sport}/${organizationId}/leaders-qualification/${leaderCriteriaId}/qualifiers/manage`;

  return (
    <Page title={pageTitle} tabTitle={pageTitle}>
      {ruleToEdit && <LeaderCriteriaRuleEditModal rule={ruleToEdit} onClose={() => setRuleToEdit(null)} />}
      <DataTable<LeaderCriteriaRule>
        handleRefresh={() => {
          void refetch();
        }}
        title={formatMessage({ id: "rules" })}
        columns={columns}
        rowActions={{
          base: {
            manage: {
              onClick: (row) => setRuleToEdit(row),
            },
            delete: {
              onClick: (row) => deleteLeaderCriteriaRule(row.qualifierId),
              itemName: (row) => getStatisticFieldLabel(row.statisticField),
              loading: isDeletePending,
            },
          },
        }}
        rowKey={"qualifierId"}
        rowNameKey={"statisticField"}
        dataSource={data?.data || []}
        columnChooserLocalStorageKey={`${sport}.tables.settings.columns.leaderCriteriaRules`}
        isDataLoading={isPending}
        handleAdd={() => navigate(getNavigateUrl(leaderCriteriaId))}
        addButtonIntlKey={"rule"}
        permissionNames={[PermissionNames.LEADER_CRITERIA_SET]}
        dataTableUiConfiguration={dataTableUiConfiguration}
        paginationConfig={{ ...paginationConfig, hasNext: data?.hasNextLink }}
      />
    </Page>
  );
};

export default LeaderCriteriaRuleListPage;
